import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Safely converts string or number to number
 * @param value - String or number value to convert
 * @param fallback - Fallback value if conversion fails (default: 0)
 * @returns Converted number value
 */
export function toNumber(value: string | number | undefined | null, fallback = 0): number {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Format a number for display (handles null/undefined, optional decimals/locale).
 */
export function formatNumber(
  value: number | string | undefined | null,
  options?: { decimals?: number; locale?: string },
): string {
  const num = toNumber(value);
  const { decimals, locale = 'en-US' } = options ?? {};
  if (decimals !== undefined) {
    return num.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }
  return num.toLocaleString(locale);
}

/**
 * Strips HTML tags and decodes common HTML entities to return plain text.
 */
export function stripHtml(html?: string | null): string {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  }
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Strips out '_lite' from a VNC console URL (e.g., 'vnc_lite.html' -> 'vnc.html')
 */
export function stripVncLite(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.replace('vnc_lite.html', 'vnc.html');
}

// Re-export utility functions
export { extractErrorMessage } from "./utils/error"
export { formatDateTime, formatDate } from "./utils/date"
// Note: fetchWithAuth was removed. Use `axiosClientAPI` from '@/lib/axios' directly when needed.
